import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// Mar 2026 — was `process.env.REACT_APP_BACKEND_URL` (missing the
// `/api` suffix), which silently routed every call to the front-end
// dev server instead of FastAPI.  Axios then parsed the returned
// `index.html` payload as an empty array and the dropdown rendered
// "No messages yet" even though the legacy dashboard's parallel
// fetcher (which DOES include `/api`) was getting threads back.
// User-reported: "on the messages dropdown in v2 i am not seeing the
// messages".  Same bug surface exists nowhere else in the codebase
// — every other module reads ${BACKEND_URL}/api consistently.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custom hook for managing all messaging state and operations
 */
export const useMessaging = (user, wsConnected) => {
  // Core messaging state
  const [showStaffMessages, setShowStaffMessages] = useState(false);
  const [staffMessageThreads, setStaffMessageThreads] = useState([]);
  const [selectedStaffThread, setSelectedStaffThread] = useState(null);
  const [staffThreadMessages, setStaffThreadMessages] = useState([]);
  const [staffReplyText, setStaffReplyText] = useState('');
  const [sendingStaffReply, setSendingStaffReply] = useState(false);
  const [loadingStaffMessages, setLoadingStaffMessages] = useState(false);
  const [unreadStaffMessagesCount, setUnreadStaffMessagesCount] = useState(0);
  
  // Forward dialog state
  const [showForwardDialog, setShowForwardDialog] = useState(false);
  const [forwardToUserId, setForwardToUserId] = useState('');
  const [forwardingMessage, setForwardingMessage] = useState(false);
  const [threadToForward, setThreadToForward] = useState(null);
  const [forwardNote, setForwardNote] = useState('');
  
  // Menu state
  const [openMessageMenu, setOpenMessageMenu] = useState(null);
  
  // New message dialog state
  const [showStaffNewMessageDialog, setShowStaffNewMessageDialog] = useState(false);
  const [staffNewMessageClientId, setStaffNewMessageClientId] = useState('');
  const [staffNewMessageSubject, setStaffNewMessageSubject] = useState('');
  const [staffNewMessageText, setStaffNewMessageText] = useState('');
  const [staffNewMessageTopic, setStaffNewMessageTopic] = useState('general');
  const [sendingStaffNewMessage, setSendingStaffNewMessage] = useState(false);
  
  // Staff-to-staff messaging
  const [showStaffToStaffMessageDialog, setShowStaffToStaffMessageDialog] = useState(false);
  const [staffToStaffRecipientId, setStaffToStaffRecipientId] = useState('');
  const [staffToStaffRecipientName, setStaffToStaffRecipientName] = useState('');
  const [staffToStaffSubject, setStaffToStaffSubject] = useState('');
  const [staffToStaffMessage, setStaffToStaffMessage] = useState('');
  const [sendingStaffToStaffMessage, setSendingStaffToStaffMessage] = useState(false);
  const [staffToStaffTopic, setStaffToStaffTopic] = useState('general');
  
  // Message from chart
  const [showMessageFromChartDialog, setShowMessageFromChartDialog] = useState(false);
  const [messageFromChartClientId, setMessageFromChartClientId] = useState('');
  const [messageFromChartClientName, setMessageFromChartClientName] = useState('');
  const [messageRecipientType, setMessageRecipientType] = useState('client');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  
  // Broadcast
  const [showBroadcastConfirmation, setShowBroadcastConfirmation] = useState(false);
  const [broadcastClientCount, setBroadcastClientCount] = useState(0);
  
  // Recall dialog
  const [showRecallDialog, setShowRecallDialog] = useState(false);
  const [recallThreadId, setRecallThreadId] = useState(null);
  const [recallReason, setRecallReason] = useState('');
  const [recallingMessage, setRecallingMessage] = useState(false);
  
  // Archived messages
  const [showArchivedMessagesDialog, setShowArchivedMessagesDialog] = useState(false);
  const [archivedSearchClientName, setArchivedSearchClientName] = useState('');
  const [archivedSearchStaffName, setArchivedSearchStaffName] = useState('');
  const [archivedSearchStartDate, setArchivedSearchStartDate] = useState('');
  const [archivedSearchEndDate, setArchivedSearchEndDate] = useState('');
  const [archivedSearchKeyword, setArchivedSearchKeyword] = useState('');
  const [archivedMessageResults, setArchivedMessageResults] = useState([]);
  const [searchingArchived, setSearchingArchived] = useState(false);
  const [selectedArchivedThread, setSelectedArchivedThread] = useState(null);
  const [archivedThreadMessages, setArchivedThreadMessages] = useState([]);
  
  // Message templates
  const [showMessageTemplatesDialog, setShowMessageTemplatesDialog] = useState(false);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({ name: '', subject: '', message: '' });
  
  // Ref for tracking message changes
  const lastMessageCheckRef = useRef({ unread: 0, lastMessageAt: null });
  
  // Fetch message threads
  const fetchStaffMessageThreads = useCallback(async (showLoading = true) => {
    if (!user) return;
    try {
      if (showLoading) setLoadingStaffMessages(true);
      const response = await axios.get(`${API}/staff/messages`);
      setStaffMessageThreads(response.data || []);
      
      // Calculate unread count
      const unread = (response.data || []).reduce((sum, thread) => sum + (thread.unread_count_staff || 0), 0);
      setUnreadStaffMessagesCount(unread);
      
      // Update the check reference
      const lastMessage = response.data?.[0];
      lastMessageCheckRef.current = {
        unread,
        lastMessageAt: lastMessage?.last_message_at || null
      };
    } catch (error) {
      console.error('Error fetching staff messages:', error);
    } finally {
      if (showLoading) setLoadingStaffMessages(false);
    }
  }, [user]);
  
  // Lightweight check for new messages
  const checkForNewMessages = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/staff/messages/check`);
      const { unread_count, last_message_at } = response.data;
      
      if (unread_count !== lastMessageCheckRef.current.unread || 
          last_message_at !== lastMessageCheckRef.current.lastMessageAt) {
        void 0 && console.log('[Messages] Change detected, refetching...');
        await fetchStaffMessageThreads(false);
      }
    } catch (error) {
      console.error('Error checking messages:', error);
    }
  }, [user, fetchStaffMessageThreads]);
  
  // Fetch messages for a specific thread
  const fetchStaffThreadMessages = async (threadId) => {
    try {
      const response = await axios.get(`${API}/staff/messages/${threadId}`);
      setSelectedStaffThread(response.data.thread);
      setStaffThreadMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching thread messages:', error);
      toast.error('Failed to load messages');
    }
  };
  
  // Send reply to a thread
  const sendStaffReply = async () => {
    if (!staffReplyText.trim() || !selectedStaffThread) return;
    
    const messageText = staffReplyText;
    setSendingStaffReply(true);
    
    // Optimistic UI update
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      thread_id: selectedStaffThread.id,
      sender_id: user.id,
      sender_name: user.full_name,
      sender_type: 'staff',
      message: messageText,
      created_at: new Date().toISOString(),
      attachments: []
    };
    setStaffThreadMessages(prev => [...prev, optimisticMessage]);
    setStaffReplyText('');
    
    try {
      await axios.post(
        `${API}/staff/messages/${selectedStaffThread.id}/reply`,
        { message: messageText, attachments: [] }
      );
      toast.success('Reply sent');
      fetchStaffThreadMessages(selectedStaffThread.id);
      fetchStaffMessageThreads(false);
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
      setStaffThreadMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
      setStaffReplyText(messageText);
    } finally {
      setSendingStaffReply(false);
    }
  };
  
  // Forward a message
  const forwardStaffMessage = async () => {
    const threadToUse = threadToForward || selectedStaffThread;
    if (!forwardToUserId || !threadToUse) {
      toast.error('Please select a staff member');
      return;
    }
    
    setForwardingMessage(true);
    try {
      const params = new URLSearchParams({ forward_to_user_id: forwardToUserId });
      if (forwardNote.trim()) {
        params.append('forward_note', forwardNote.trim());
      }
      
      const response = await axios.post(
        `${API}/staff/messages/${threadToUse.id}/forward?${params.toString()}`
      );
      toast.success(response.data.message || 'Message forwarded successfully');
      setShowForwardDialog(false);
      setForwardToUserId('');
      setForwardNote('');
      setThreadToForward(null);
      setOpenMessageMenu(null);
      fetchStaffMessageThreads(false);
    } catch (error) {
      console.error('Error forwarding message:', error);
      toast.error('Failed to forward message');
    } finally {
      setForwardingMessage(false);
    }
  };
  
  // Recall a message
  const recallStaffMessage = async () => {
    if (!recallReason.trim()) {
      toast.error('Please provide a reason for recall');
      return;
    }
    
    setRecallingMessage(true);
    try {
      const params = new URLSearchParams({ recall_reason: recallReason });
      const response = await axios.post(
        `${API}/staff/messages/${recallThreadId}/recall?${params.toString()}`
      );
      toast.success(response.data.message || 'Message recalled');
      setShowRecallDialog(false);
      setRecallReason('');
      setRecallThreadId(null);
      fetchStaffMessageThreads(false);
    } catch (error) {
      console.error('Error recalling message:', error);
      toast.error(error.response?.data?.detail || 'Failed to recall message');
    } finally {
      setRecallingMessage(false);
    }
  };
  
  // Send broadcast message
  const sendBroadcastMessage = async () => {
    if (!staffNewMessageSubject.trim() || !staffNewMessageText.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSendingStaffNewMessage(true);
    try {
      const params = new URLSearchParams({
        subject: staffNewMessageSubject,
        message: staffNewMessageText,
        topic: staffNewMessageTopic
      });
      
      const response = await axios.post(`${API}/staff/messages/broadcast?${params.toString()}`);
      toast.success(response.data.message || 'Broadcast sent successfully');
      setShowStaffNewMessageDialog(false);
      setShowBroadcastConfirmation(false);
      setStaffNewMessageClientId('');
      setStaffNewMessageSubject('');
      setStaffNewMessageText('');
      setStaffNewMessageTopic('general');
      fetchStaffMessageThreads(false);
    } catch (error) {
      console.error('Error sending broadcast:', error);
      toast.error('Failed to send broadcast message');
    } finally {
      setSendingStaffNewMessage(false);
    }
  };
  
  // WebSocket handler for new messages (to be called from parent)
  const handleWebSocketMessage = useCallback((threadId, message) => {
    void 0 && console.log('[WebSocket] New message received for thread:', threadId);
    
    // Add message to current thread if viewing it
    setStaffThreadMessages(prev => {
      if (prev.length > 0 && prev[0]?.thread_id === threadId) {
        const exists = prev.some(m => m.id === message.id);
        if (!exists) {
          return [...prev, message];
        }
      }
      return prev;
    });
    
    // Update thread list to show new message preview
    setStaffMessageThreads(prev => prev.map(thread => {
      if (thread.id === threadId) {
        return {
          ...thread,
          last_message_at: message.created_at,
          last_message_preview: message.message?.substring(0, 100) || '',
          unread_count_staff: (thread.unread_count_staff || 0) + 1
        };
      }
      return thread;
    }));
    
    // Increment unread count
    setUnreadStaffMessagesCount(prev => prev + 1);
  }, []);
  
  // Polling effect for messages
  useEffect(() => {
    if (user) {
      fetchStaffMessageThreads(true);
      const pollInterval = wsConnected ? 60000 : 15000;
      const interval = setInterval(() => {
        if (!document.hidden) {
          checkForNewMessages();
        }
      }, pollInterval);
      return () => clearInterval(interval);
    }
  }, [user, fetchStaffMessageThreads, checkForNewMessages, wsConnected]);
  
  // Reset selected thread when closing messages
  const closeMessagesDropdown = useCallback(() => {
    setShowStaffMessages(false);
  }, []);
  
  const openThread = useCallback((thread) => {
    setSelectedStaffThread(thread);
    fetchStaffThreadMessages(thread.id);
  }, []);
  
  const closeThread = useCallback(() => {
    setSelectedStaffThread(null);
    setStaffThreadMessages([]);
    setStaffReplyText('');
  }, []);
  
  return {
    // Core state
    showStaffMessages,
    setShowStaffMessages,
    staffMessageThreads,
    setStaffMessageThreads,
    selectedStaffThread,
    setSelectedStaffThread,
    staffThreadMessages,
    setStaffThreadMessages,
    staffReplyText,
    setStaffReplyText,
    sendingStaffReply,
    loadingStaffMessages,
    unreadStaffMessagesCount,
    
    // Forward state
    showForwardDialog,
    setShowForwardDialog,
    forwardToUserId,
    setForwardToUserId,
    forwardingMessage,
    threadToForward,
    setThreadToForward,
    forwardNote,
    setForwardNote,
    
    // Menu state
    openMessageMenu,
    setOpenMessageMenu,
    
    // New message dialog
    showStaffNewMessageDialog,
    setShowStaffNewMessageDialog,
    staffNewMessageClientId,
    setStaffNewMessageClientId,
    staffNewMessageSubject,
    setStaffNewMessageSubject,
    staffNewMessageText,
    setStaffNewMessageText,
    staffNewMessageTopic,
    setStaffNewMessageTopic,
    sendingStaffNewMessage,
    setSendingStaffNewMessage,
    
    // Staff-to-staff
    showStaffToStaffMessageDialog,
    setShowStaffToStaffMessageDialog,
    staffToStaffRecipientId,
    setStaffToStaffRecipientId,
    staffToStaffRecipientName,
    setStaffToStaffRecipientName,
    staffToStaffSubject,
    setStaffToStaffSubject,
    staffToStaffMessage,
    setStaffToStaffMessage,
    sendingStaffToStaffMessage,
    setSendingStaffToStaffMessage,
    staffToStaffTopic,
    setStaffToStaffTopic,
    
    // Message from chart
    showMessageFromChartDialog,
    setShowMessageFromChartDialog,
    messageFromChartClientId,
    setMessageFromChartClientId,
    messageFromChartClientName,
    setMessageFromChartClientName,
    messageRecipientType,
    setMessageRecipientType,
    clientSearchQuery,
    setClientSearchQuery,
    
    // Broadcast
    showBroadcastConfirmation,
    setShowBroadcastConfirmation,
    broadcastClientCount,
    setBroadcastClientCount,
    
    // Recall
    showRecallDialog,
    setShowRecallDialog,
    recallThreadId,
    setRecallThreadId,
    recallReason,
    setRecallReason,
    recallingMessage,
    
    // Archived
    showArchivedMessagesDialog,
    setShowArchivedMessagesDialog,
    archivedSearchClientName,
    setArchivedSearchClientName,
    archivedSearchStaffName,
    setArchivedSearchStaffName,
    archivedSearchStartDate,
    setArchivedSearchStartDate,
    archivedSearchEndDate,
    setArchivedSearchEndDate,
    archivedSearchKeyword,
    setArchivedSearchKeyword,
    archivedMessageResults,
    setArchivedMessageResults,
    searchingArchived,
    setSearchingArchived,
    selectedArchivedThread,
    setSelectedArchivedThread,
    archivedThreadMessages,
    setArchivedThreadMessages,
    
    // Templates
    showMessageTemplatesDialog,
    setShowMessageTemplatesDialog,
    messageTemplates,
    setMessageTemplates,
    editingTemplate,
    setEditingTemplate,
    templateFormData,
    setTemplateFormData,
    
    // Functions
    fetchStaffMessageThreads,
    fetchStaffThreadMessages,
    sendStaffReply,
    forwardStaffMessage,
    recallStaffMessage,
    sendBroadcastMessage,
    handleWebSocketMessage,
    closeMessagesDropdown,
    openThread,
    closeThread,
  };
};

export default useMessaging;
